/**
 * Admin write schemas for WebsiteNavigationMenu and WebsiteNavigationItem
 * (Phase 5 - CMS write API).
 *
 * Reorder body is a FLAT list of {id, parentId, sortOrder} so a single
 * call can move an item between parents and renumber siblings at once
 * (drag-and-drop tree editing).
 */

import { z } from 'zod';

import { UuidSchema } from './common.js';

export const WebsiteNavigationMenuLocationSchema = z.enum([
  'HEADER_PRIMARY',
  'HEADER_SECONDARY',
  'FOOTER_CONTENT',
  'FOOTER_QUICK_LINKS',
  'FOOTER_POPULAR_DESTINATIONS',
  'FOOTER_DOMESTIC_SEO',
  'FOOTER_INTERNATIONAL_SEO',
  'FOOTER_HELP',
  'MOBILE_DRAWER',
]);
export type WebsiteNavigationMenuLocation = z.infer<typeof WebsiteNavigationMenuLocationSchema>;

export const CreateNavigationMenuRequestSchema = z.object({
  location: WebsiteNavigationMenuLocationSchema,
  title: z.string().min(1).max(120),
});
export type CreateNavigationMenuRequest = z.infer<typeof CreateNavigationMenuRequestSchema>;

export const UpdateNavigationMenuRequestSchema = z.object({
  title: z.string().min(1).max(120),
});
export type UpdateNavigationMenuRequest = z.infer<typeof UpdateNavigationMenuRequestSchema>;

export const CreateNavigationItemRequestSchema = z.object({
  menuId: UuidSchema,
  parentId: UuidSchema.nullish(),
  label: z.string().min(1).max(120),
  href: z.string().max(2048).nullish(),
  pageId: UuidSchema.nullish(),
  iconKey: z.string().max(60).nullish(),
  openInNewTab: z.boolean().default(false),
  sortOrder: z.number().int().min(0).default(0),
  isVisible: z.boolean().default(true),
});
export type CreateNavigationItemRequest = z.infer<typeof CreateNavigationItemRequestSchema>;

export const UpdateNavigationItemRequestSchema = CreateNavigationItemRequestSchema.omit({
  menuId: true,
}).partial();
export type UpdateNavigationItemRequest = z.infer<typeof UpdateNavigationItemRequestSchema>;

export const ReorderNavigationItemsRequestSchema = z.object({
  items: z
    .array(
      z.object({
        id: UuidSchema,
        parentId: UuidSchema.nullable(),
        sortOrder: z.number().int().min(0),
      }),
    )
    .min(1)
    .max(500),
});
export type ReorderNavigationItemsRequest = z.infer<typeof ReorderNavigationItemsRequestSchema>;
